import {DateTime, Diagram, DiagramType, nominate} from "platformed-client/types";

// IT Access Provisioning Process Diagram
// Demonstrates user access management lifecycle with segregation of duties
export const itAccessProvisioning: Diagram = {
  diagram_id: nominate("DiagramId", "hardcoded-it-access-001"),
  name: "IT Access Provisioning Process",
  description:
    "User access lifecycle management demonstrating ITGCs from request through provisioning, review, and termination. Shows multi-level approval process and segregation of duties review required before granting system access.",
  diagram_type: "ProcessFlow" as DiagramType,
  created_at: "2025-10-02T13:00:00Z" as DateTime,
  updated_at: "2025-11-05T11:30:00Z" as DateTime,
  owner: null,
  deleted: null,
  lanes_enabled: false,
  lanes: {},
  generation_state: {type: "NotStarted"},

  nodes: {
    request: {
      diagram_node_id: nominate("DiagramNodeId", "it-request"),
      label: "Access Request",
      description: "Employee or manager submits access request for system/application",
      icon: "isoflow.document",
      position: [2, 2], // Requestor swimlane
    },
    managerApproval: {
      diagram_node_id: nominate("DiagramNodeId", "it-manager-approval"),
      label: "Manager Approval",
      description: "Direct manager approves business need for access\n\nControls\nIT-01 Manager Business-Need Approval",
      icon: "isoflow.block",
      position: [10, 2], // Manager swimlane
    },
    systemOwnerApproval: {
      diagram_node_id: nominate("DiagramNodeId", "it-system-approval"),
      label: "System Owner Approval",
      description:
        "System/data owner approves appropriate access level\n\nControls\nIT-02 System Owner Technical Approval",
      icon: "isoflow.block",
      position: [18, 2], // System Owner swimlane
    },
    roleAssignment: {
      diagram_node_id: nominate("DiagramNodeId", "it-role-assignment"),
      label: "Role Assignment",
      description:
        "IT Security determines appropriate role based on least privilege\n\nControls\nIT-03 Security SoD Validation",
      icon: "isoflow.document",
      position: [26, 2], // IT Security swimlane
    },
    provisioning: {
      diagram_node_id: nominate("DiagramNodeId", "it-provisioning"),
      label: "Account Provisioning",
      description:
        "IT Operations creates account and assigns permissions\n\nControls\nIT-04 Least Privilege Role-Based Assignment",
      icon: "isoflow.user",
      position: [34, 2], // IT Operations swimlane
    },
    notification: {
      diagram_node_id: nominate("DiagramNodeId", "it-notification"),
      label: "User Notification",
      description:
        "User receives credentials and training materials\n\nControls\nIT-05 Formal Credential and Training Issuance",
      icon: "isoflow.document",
      position: [34, 6], // IT Operations swimlane (lower)
    },
    periodicReview: {
      diagram_node_id: nominate("DiagramNodeId", "it-review"),
      label: "Periodic Access Review",
      description: "Compliance conducts quarterly access review\n\nControls\nIT-06 Quarterly Access Re-certification",
      icon: "fi.storage.015.book",
      position: [42, 2], // Compliance swimlane
    },
    reviewAction: {
      diagram_node_id: nominate("DiagramNodeId", "it-review-action"),
      label: "Review Certification",
      description: "Manager and system owner certify or revoke access",
      icon: "isoflow.block",
      position: [42, -4], // Compliance swimlane (upper)
    },
    termination: {
      diagram_node_id: nominate("DiagramNodeId", "it-termination"),
      label: "Termination Trigger",
      description:
        "HR notifies IT of employee termination or transfer\n\nControls\nIT-08 HR-Authoritative Termination Trigger",
      icon: "isoflow.server",
      position: [50, 2], // HR swimlane
    },
    deactivation: {
      diagram_node_id: nominate("DiagramNodeId", "it-deactivation"),
      label: "Access Deactivation",
      description:
        "IT Operations immediately disables all system access\n\nControls\nIT-07 Remediation of Review Exceptions\nIT-09 Timely Access Deactivation SLA",
      icon: "isoflow.user",
      position: [34, -4], // IT Operations swimlane (upper)
    },
  },

  connections: {
    "it-1": {
      diagram_connection_id: nominate("DiagramConnectionId", "it-conn-1"),
      source_node_id: nominate("DiagramNodeId", "it-request"),
      target_node_id: nominate("DiagramNodeId", "it-manager-approval"),
      label: "Access Request",
      color: "blue",
      anchors: [],
    },
    "it-2": {
      diagram_connection_id: nominate("DiagramConnectionId", "it-conn-2"),
      source_node_id: nominate("DiagramNodeId", "it-manager-approval"),
      target_node_id: nominate("DiagramNodeId", "it-system-approval"),
      label: "Manager Approved",
      color: "green",
      anchors: [],
    },
    "it-3": {
      diagram_connection_id: nominate("DiagramConnectionId", "it-conn-3"),
      source_node_id: nominate("DiagramNodeId", "it-system-approval"),
      target_node_id: nominate("DiagramNodeId", "it-role-assignment"),
      label: "Approved Request",
      color: "green",
      anchors: [],
    },
    "it-4": {
      diagram_connection_id: nominate("DiagramConnectionId", "it-conn-4"),
      source_node_id: nominate("DiagramNodeId", "it-role-assignment"),
      target_node_id: nominate("DiagramNodeId", "it-provisioning"),
      label: "Role Assignment",
      color: "purple",
      anchors: [],
    },
    "it-5": {
      diagram_connection_id: nominate("DiagramConnectionId", "it-conn-5"),
      source_node_id: nominate("DiagramNodeId", "it-provisioning"),
      target_node_id: nominate("DiagramNodeId", "it-notification"),
      label: "Account Created",
      color: "green",
      anchors: [],
    },
    "it-6": {
      diagram_connection_id: nominate("DiagramConnectionId", "it-conn-6"),
      source_node_id: nominate("DiagramNodeId", "it-notification"),
      target_node_id: nominate("DiagramNodeId", "it-review"),
      label: "Active Access",
      color: "blue",
      anchors: [],
    },
    "it-7": {
      diagram_connection_id: nominate("DiagramConnectionId", "it-conn-7"),
      source_node_id: nominate("DiagramNodeId", "it-review"),
      target_node_id: nominate("DiagramNodeId", "it-review-action"),
      label: "Access Report",
      color: "blue",
      anchors: [],
    },
    "it-8": {
      diagram_connection_id: nominate("DiagramConnectionId", "it-conn-8"),
      source_node_id: nominate("DiagramNodeId", "it-review-action"),
      target_node_id: nominate("DiagramNodeId", "it-deactivation"),
      label: "Revoke Access",
      color: "red",
      anchors: [],
    },
    "it-9": {
      diagram_connection_id: nominate("DiagramConnectionId", "it-conn-9"),
      source_node_id: nominate("DiagramNodeId", "it-termination"),
      target_node_id: nominate("DiagramNodeId", "it-deactivation"),
      label: "Termination Notice",
      color: "red",
      anchors: [],
    },
  },

  regions: {},
  engagement: {
    engagement_id: nominate("EngagementId", "p2p-engagement-1"),
    name: "",
    third_party: {name: "", third_party_id: nominate("ThirdPartyId", "p2p-third-party-1")},
  },
};
